import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useB3Config } from "../../../../../global-account/react/index.js";
import { debugB3React } from "../../../../../shared/utils/debug.js";
import { useState } from "react";
import { notificationsAPI } from "../../../utils/notificationsAPI.js";
import { toast } from "../../Toast/toastApi.js";
import { NotificationChannel } from "../NotificationChannel.js";
const debug = debugB3React("TelegramChannel");
export const TelegramChannel = ({
  userId,
  jwtToken,
  telegramChannel,
  isConnected,
  isOptimisticallyConnected,
  onConnectionChange,
  onToggle
}) => {
  const {
    partnerId
  } = useB3Config();
  const [isConnecting, setIsConnecting] = useState(false);
  const [status, setStatus] = useState("idle");
  // Detect if we're disconnecting
  const isDisconnecting = isConnected && !isOptimisticallyConnected;
  const handleConnect = async () => {
    if (!userId || !jwtToken) return;
    try {
      setIsConnecting(true);
      const {
        deepLink
      } = await notificationsAPI.getTelegramLink(jwtToken);
      window.open(deepLink, "_blank");
      setStatus("pending");
      // Poll for connection using nested timeouts to avoid overlapping requests
      const startTime = Date.now();
      const maxDuration = 120000; // 2 minutes
      let timeoutId = null;
      const pollStatus = async () => {
        try {
          const {
            connected
          } = await notificationsAPI.checkTelegramStatus(userId, jwtToken);
          if (connected) {
            if (timeoutId) clearTimeout(timeoutId);
            setStatus("connected");
            await notificationsAPI.ensureNotificationSettings(userId, partnerId, "general", jwtToken);
            toast.success("Telegram connected successfully!");
            onConnectionChange();
            return;
          }
        } catch (err_0) {
          debug("Error checking Telegram status:", err_0);
        }
        // Check if we've exceeded max duration
        if (Date.now() - startTime < maxDuration) {
          // Schedule next check after previous one completes
          timeoutId = setTimeout(pollStatus, 2000);
        } else {
          setStatus("idle");
        }
      };
      // Start polling
      timeoutId = setTimeout(pollStatus, 2000);
    } catch (err) {
      debug("Error connecting Telegram:", err);
      toast.error("Failed to connect Telegram");
    } finally {
      setIsConnecting(false);
    }
  };
  const handleToggle = () => {
    if (isConnected) {
      setStatus("idle");
      onToggle(true);
    } else {
      handleConnect();
    }
  };
  const icon = _jsx("svg", {
    width: "1000px",
    height: "1000px",
    viewBox: "0 0 1000 1000",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    children: _jsx("g", {
      id: "Telegram",
      stroke: "none",
      strokeWidth: "1",
      fill: "none",
      fillRule: "evenodd",
      children: _jsx("path", {
        d: "M226.328419,494.722069 C372.088573,431.216685 469.284839,389.350049 517.917216,369.122161 C656.772535,311.36743 685.625481,301.334815 704.431427,301.003532 C708.567621,300.93067 717.815839,301.955743 723.806446,306.816707 C728.864797,310.92121 730.256552,316.46581 730.922551,320.357329 C731.588551,324.248848 732.417879,333.113828 731.758626,340.040666 C724.234007,419.102486 691.675104,610.964674 675.110982,699.515267 C668.10208,736.984342 654.301336,749.547532 640.940618,750.777006 C611.904684,753.448938 589.856115,731.588035 561.733393,713.153237 C517.726886,684.306416 492.866009,666.349181 450.150074,638.200013 C400.78442,605.66878 432.786119,587.789048 460.919462,558.568563 C468.282091,550.921423 596.21508,434.556479 598.691227,424.000355 C599.00091,422.680135 599.288312,417.758981 596.36474,415.160431 C593.441168,412.561881 589.126229,413.450484 586.012448,414.157198 C581.598758,415.158943 511.297793,461.625274 375.109553,553.556189 C355.154858,567.258623 337.080515,573.934908 320.886524,573.585046 C303.033948,573.199351 268.692754,563.490928 243.163606,555.192408 C211.851067,545.013936 186.964484,539.632504 189.131547,522.346309 C190.260287,513.342589 202.659244,504.134509 226.328419,494.722069 Z",
        id: "Path-3",
        fill: "#FFFFFF"
      })
    })
  });
  const inputSection = status === "pending" ? _jsxs("div", {
    className: "mt-1",
    children: [_jsx("p", {
      className: "font-['PP_Neue_Montreal','PP_Neue_Montreal_Fallback',sans-serif] text-[14px] leading-[20px] text-yellow-700 dark:text-white",
      children: "Waiting for connection..."
    }), _jsx("p", {
      className: "font-['PP_Neue_Montreal','PP_Neue_Montreal_Fallback',sans-serif] text-[12px] text-yellow-600 dark:text-white",
      children: "Send /start to @b3_notifications_bot"
    })]
  }) : _jsxs("button", {
    onClick: handleConnect,
    disabled: isConnecting,
    className: "mt-1 flex items-center gap-1 disabled:opacity-50",
    children: [_jsx("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 16 16",
      fill: "none",
      children: _jsx("path", {
        d: "M8 3.33333V12.6667M3.33333 8H12.6667",
        stroke: "#0c68e9",
        strokeWidth: "1.5",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      })
    }), _jsx("span", {
      className: "font-['Inter',sans-serif] text-[11px] font-semibold leading-[16px] text-[#0b57c2]",
      children: isConnecting ? "Opening..." : "Add Telegram"
    })]
  });
  return _jsx(NotificationChannel, {
    icon: icon,
    iconClassName: "bg-[#24A1DE] rounded-xl",
    title: "Telegram",
    isConnected: isOptimisticallyConnected || status === "connected",
    isConnecting: isConnecting,
    isDisconnecting: isDisconnecting,
    connectedInfo: telegramChannel?.channel_identifier || "Connected",
    inputSection: inputSection,
    onToggle: handleToggle
  });
};